import { PDFDraggablePanelContext } from "components/utils/Contexts";
import { debounce } from "lodash";
import { useCallback, useContext, useEffect, useState } from "react";

interface EdgeHandlesProps {
  pdfPanelRef: React.RefObject<HTMLDivElement>;
  size: {
    width: number;
    height: number;
  };
  isResizing: boolean;
  isDraggable: boolean;
  setSize: React.Dispatch<
    React.SetStateAction<{
      width: number;
      height: number;
    }>
  >;
  setIsResizing: React.Dispatch<React.SetStateAction<boolean>>;
  setPanelPosition?: React.Dispatch<
    React.SetStateAction<{ x: number; y: number }>
  >;
}

const EdgeHandles: React.FC<EdgeHandlesProps> = ({
  pdfPanelRef,
  size,
  isResizing,
  isDraggable,
  setSize,
  setIsResizing,
  setPanelPosition,
}) => {
  // States definition
  const [currentHandle, setCurrentHandle] = useState<
    "n" | "s" | "w" | "e" | "nw" | "ne" | "sw" | "se" | null
  >(null);
  const [startResizeMousePosition, setStartResizeMousePosition] = useState({
    x: 0,
    y: 0,
  });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const [startResizePanelPosition, setStartResizePanelPosition] = useState({
    x: 0,
    y: 0,
  });

  // Get the parameters from context
  const { MIN_PANEL_WIDTH, MIN_PANEL_HEIGHT } = useContext(
    PDFDraggablePanelContext
  );

  /**
   * Function used to manage the resizing of the element
   */
  const handleResizeMouseMove = useCallback(
    debounce((event: any) => {
      // Check if we are resizing
      if (!isResizing) return;
      // Check if it's possible to resize
      if (!isDraggable) return;

      // Define the delta
      const deltaX = event.clientX - startResizeMousePosition.x;
      const deltaY = event.clientY - startResizeMousePosition.y;

      // Define the output parameters
      let newWidth = startSize.width;
      let newHeight = startSize.height;
      let newX = startResizePanelPosition.x;
      let newY = startResizePanelPosition.y;

      // Resize based on the selected handle
      switch (currentHandle) {
        case "e":
          newWidth = Math.max(startSize.width + deltaX, MIN_PANEL_WIDTH);
          break;
        case "w":
          // Check if the limit was achieved
          if (startSize.width - deltaX > MIN_PANEL_WIDTH) {
            newWidth = startSize.width - deltaX;
            newX = startResizePanelPosition.x + deltaX;
          } else {
            newWidth = MIN_PANEL_WIDTH;
            // Add the limit deltaX
            newX = newX + (startSize.width - MIN_PANEL_WIDTH);
          }
          break;
        case "s":
          newHeight = Math.max(startSize.height + deltaY, MIN_PANEL_HEIGHT);
          break;
        case "n":
          // Check if the limit was achieved
          if (startSize.height - deltaY > MIN_PANEL_HEIGHT) {
            newHeight = startSize.height - deltaY;
            newY = startResizePanelPosition.y + deltaY;
          } else {
            newHeight = MIN_PANEL_HEIGHT;
            // Add the limit deltaX
            newY = newY + (startSize.height - MIN_PANEL_HEIGHT);
          }
          break;
        case "se":
          newWidth = Math.max(startSize.width + deltaX, MIN_PANEL_WIDTH);
          newHeight = Math.max(startSize.height + deltaY, MIN_PANEL_HEIGHT);
          break;
        case "sw":
          newHeight = Math.max(startSize.height + deltaY, MIN_PANEL_HEIGHT);

          // Check if the limit was achieved
          if (startSize.width - deltaX > MIN_PANEL_WIDTH) {
            newWidth = startSize.width - deltaX;
            newX = startResizePanelPosition.x + deltaX;
          } else {
            newWidth = MIN_PANEL_WIDTH;
            // Add the limit deltaX
            newX = newX + (startSize.width - MIN_PANEL_WIDTH);
          }
          break;
        case "ne":
          newWidth = Math.max(startSize.width + deltaX, MIN_PANEL_WIDTH);

          // Check if the limit was achieved
          if (startSize.height - deltaY > MIN_PANEL_HEIGHT) {
            newHeight = startSize.height - deltaY;
            newY = startResizePanelPosition.y + deltaY;
          } else {
            newHeight = MIN_PANEL_HEIGHT;
            // Add the limit deltaX
            newY = newY + (startSize.height - MIN_PANEL_HEIGHT);
          }
          break;
        case "nw":
          // Check if the limit was achieved for the x dimension
          if (startSize.width - deltaX > MIN_PANEL_WIDTH) {
            newWidth = startSize.width - deltaX;
            newX = startResizePanelPosition.x + deltaX;
          } else {
            newWidth = MIN_PANEL_WIDTH;
            // Add the limit deltaX
            newX = newX + (startSize.width - MIN_PANEL_WIDTH);
          }

          // Check if the limit was achieved for the y dimension
          if (startSize.height - deltaY > MIN_PANEL_HEIGHT) {
            newHeight = startSize.height - deltaY;
            newY = startResizePanelPosition.y + deltaY;
          } else {
            newHeight = MIN_PANEL_HEIGHT;
            // Add the limit deltaX
            newY = newY + (startSize.height - MIN_PANEL_HEIGHT);
          }
          break;
      }

      // Define the new size
      setSize({ width: newWidth, height: newHeight });
      setPanelPosition && setPanelPosition({ x: newX, y: newY });
    }),
    [
      isResizing,
      currentHandle,
      startResizeMousePosition,
      startSize,
      startResizePanelPosition,
    ]
  );

  /**
   * Function used to set up the parameters used for the resizing
   * process
   */
  const handleResizeMouseDown = (
    event: React.PointerEvent<HTMLDivElement>,
    handle: "n" | "s" | "w" | "e" | "nw" | "ne" | "sw" | "se" | null
  ) => {
    // Prevent default behaviour
    event.preventDefault();

    // Get the left and top value from the PDF panel
    const { left, top } = pdfPanelRef.current
      ? pdfPanelRef.current.getBoundingClientRect()
      : { left: 0, top: 0 };

    // Setting up
    setCurrentHandle(handle);
    setStartResizeMousePosition({ x: event.clientX, y: event.clientY });
    setStartResizePanelPosition({ x: left, y: top });
    setStartSize({ width: size.width, height: size.height });
    setIsResizing(true);
  };

  /**
   * Function used to turn off the parameters that are activated
   * during the resizing process
   */
  const handleResizeMouseUp = useCallback(() => {
    // Stop the resize state
    setIsResizing(false);
    // And reset the handle
    setCurrentHandle(null);
  }, []);

  /**
   * Activate the event listers every time the mouse moves and it's up
   */
  useEffect(() => {
    if (isResizing) {
      window.addEventListener("pointermove", handleResizeMouseMove);
      window.addEventListener("pointerup", handleResizeMouseUp);
    }

    return () => {
      window.removeEventListener("pointermove", handleResizeMouseMove);
      window.removeEventListener("pointerup", handleResizeMouseUp);
    };
  }, [isResizing, handleResizeMouseMove]);

  return (
    <>
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-3 cursor-n-resize touch-none"
        data-handle="n"
        onPointerDown={(event) => handleResizeMouseDown(event, "n")}
      />
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-3 cursor-s-resize touch-none"
        data-handle="s"
        onPointerDown={(event) => handleResizeMouseDown(event, "s")}
      />
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 h-full w-3 cursor-w-resize touch-none"
        data-handle="w"
        onPointerDown={(event) => handleResizeMouseDown(event, "w")}
      />
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 h-full w-3 cursor-e-resize touch-none"
        data-handle="e"
        onPointerDown={(event) => handleResizeMouseDown(event, "e")}
      />

      {/* Corner handles */}
      <div
        className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize touch-none"
        data-handle="nw"
        onPointerDown={(event) => handleResizeMouseDown(event, "nw")}
      />
      <div
        className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize touch-none"
        data-handle="ne"
        onPointerDown={(event) => handleResizeMouseDown(event, "ne")}
      />
      <div
        className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize touch-none"
        data-handle="sw"
        onPointerDown={(event) => handleResizeMouseDown(event, "sw")}
      />
      <div
        className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize touch-none"
        data-handle="se"
        onPointerDown={(event) => handleResizeMouseDown(event, "se")}
      />
    </>
  );
};

export default EdgeHandles;
